/**
 * Web tool-card primitives. `card-ui.native.tsx` is the iOS/Android twin.
 *
 * Every card used to lay itself out from scratch - its own paddings, its own
 * row heights, its own divider colour - so 25 cards drifted into 25 slightly
 * different designs. These are the shared atoms; a card should be a composition
 * of them, not a fresh stylesheet.
 *
 * The two files expose an IDENTICAL prop surface (`onPress`, not `onClick`;
 * `accessibilityHint`, not `title`). Metro picks the right implementation per
 * platform, but TypeScript has no `moduleSuffixes` here, so it type-checks the
 * native cards against *this* file. Any prop that exists on only one side would
 * surface as a phantom error in the other platform's cards.
 *
 * Geometry comes from the `--chat-card-*` custom properties in global.css,
 * which mirror `CARD_SHELL_SPEC`. The loading skeleton reads the same numbers,
 * which is what stops a card from changing shape when its result lands.
 */
import React, { type CSSProperties, type FC, type ReactNode } from 'react';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';

export const CARD_PADDING_X = 16;
export const CARD_PADDING_Y = 16;

/** Header row: optional leading glyph, title, optional trailing meta. */
export const CardHeader: FC<{
  title: string;
  leading?: ReactNode;
  meta?: string;
}> = ({ title, leading, meta }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: `${CARD_PADDING_Y}px ${CARD_PADDING_X}px 8px`,
    }}
  >
    {leading ? (
      <span style={{ flexShrink: 0, display: 'inline-flex', overflow: 'hidden' }}>{leading}</span>
    ) : null}
    <span
      dir="auto"
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 600,
        color: 'var(--text-primary)',
      }}
    >
      {title}
    </span>
    {meta ? (
      <span style={{ fontSize: 12, color: 'var(--text-tertiary)', flexShrink: 0 }}>{meta}</span>
    ) : null}
  </div>
);

/**
 * The atom that fixes list cards.
 *
 * The old skills rows were a name, a badge and a chevron over a single
 * ellipsised line of description - which truncated mid-word on every row and
 * left no room for the icon, flag or verification mark the data already
 * carried. This gives the subtitle two lines and a real leading slot.
 */
export const CardRow: FC<{
  title: string;
  subtitle?: string;
  meta?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  onPress?: () => void;
  accessibilityHint?: string;
  divider?: boolean;
}> = ({ title, subtitle, meta, leading, trailing, onPress, accessibilityHint, divider }) => {
  const interactive = Boolean(onPress);

  const content = (
    <>
      {leading ? (
        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{leading}</span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          dir="auto"
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            dir="auto"
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--text-secondary)',
              // Two lines, clamped. One line ellipsised every description
              // mid-word; unclamped, a long one pushed the card off-screen.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {subtitle}
          </span>
        ) : null}
        {meta ? (
          <span
            dir="auto"
            style={{
              fontSize: 11,
              lineHeight: '16px',
              color: 'var(--text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </span>
        ) : null}
      </span>
      {trailing ? (
        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          {trailing}
        </span>
      ) : null}
    </>
  );

  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    minHeight: 56,
    padding: `10px ${CARD_PADDING_X}px`,
    borderTop: divider ? '1px solid var(--input-border)' : 'none',
    borderInline: 'none',
    borderBottom: 'none',
    background: 'none',
    textAlign: 'start',
    fontFamily: 'inherit',
    color: 'inherit',
  };

  if (!interactive) {
    return <div style={style}>{content}</div>;
  }

  return (
    <button
      type="button"
      className="tabbio-card-row"
      onClick={onPress}
      title={accessibilityHint}
      style={{ ...style, cursor: 'pointer' }}
    >
      {content}
    </button>
  );
};

/**
 * Footer action, e.g. "Show all 15 skills".
 *
 * The old list cards ended in a dead count line: it told you there were more
 * than you could see and gave you no way to reach them.
 */
export const CardFooterAction: FC<{ label: string; onPress: () => void }> = ({
  label,
  onPress,
}) => (
  <button
    type="button"
    className="tabbio-card-row"
    onClick={onPress}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      padding: `10px ${CARD_PADDING_X}px`,
      borderTop: '1px solid var(--input-border)',
      borderInline: 'none',
      borderBottom: 'none',
      background: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: 13,
      color: 'var(--text-secondary)',
      textAlign: 'start',
    }}
  >
    <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
    <CardChevron />
  </button>
);

/**
 * The "opens something" chevron. Mirrors under RTL - Material Symbols has no
 * logical `chevron_forward`, so `chevron_right` would literally point backwards
 * in Arabic without the flip.
 */
export const CardChevron: FC = () => (
  <span
    className="tabbio-rtl-flip"
    style={{
      flexShrink: 0,
      color: 'var(--text-tertiary)',
      // Material Symbols draws its icon as a ligature over the literal name.
      // If the webfont is slow or blocked, an unclipped span paints the words
      // "chevron_right" in the middle of the row. Box it to the glyph.
      width: 16,
      height: 16,
      overflow: 'hidden',
      display: 'inline-flex',
    }}
  >
    <MaterialSymbol name="chevron_right" size={16} />
  </span>
);

/** Neutral pill. Never coloured - status colour is an explicit anti-pattern here. */
export const CardBadge: FC<{ children: string }> = ({ children }) => (
  <span
    style={{
      fontSize: 11,
      lineHeight: '16px',
      padding: '2px 8px',
      borderRadius: 9999,
      background: 'var(--hover)',
      color: 'var(--text-tertiary)',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

export const CardEmpty: FC<{ children: string }> = ({ children }) => (
  <div
    style={{
      padding: `${CARD_PADDING_Y + 4}px ${CARD_PADDING_X}px`,
      fontSize: 13,
      color: 'var(--text-tertiary)',
    }}
  >
    {children}
  </div>
);

/** A wrapping row of `CardAction`s at the foot of a card. */
export const CardActions: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      padding: `2px ${CARD_PADDING_X}px ${CARD_PADDING_Y}px`,
    }}
  >
    {children}
  </div>
);

/**
 * A card action: a tonal pill (`.tabbio-card-action`), never an outlined
 * chip. `busy` swaps the icon for a spinner.
 */
export const CardAction: FC<{
  label: string;
  icon?: ReactNode;
  onPress: () => void;
  accessibilityHint?: string;
  disabled?: boolean;
  busy?: boolean;
}> = ({ label, icon, onPress, accessibilityHint, disabled, busy }) => (
  <button
    type="button"
    className="tabbio-card-action"
    onClick={onPress}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
    title={accessibilityHint}
  >
    {busy ? <SpinnerGlyph color="var(--text-secondary)" size={16} /> : icon}
    {label}
  </button>
);
